import { useState, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Banknote, CheckCircle2, AlertCircle, Search, Filter, Calendar,
  ArrowUpCircle, ArrowDownCircle, Sparkles, Eye, Undo2, FileText, ExternalLink, FileSpreadsheet,
} from 'lucide-react'
import { Link } from 'react-router-dom'
import toast from 'react-hot-toast'
import { api, formatMoney } from '@/lib/api'
import { exportarExcel, numBR } from '@/lib/excel'

interface Conta {
  id: number; nome: string; tipo: string; banco: string | null
  padrao: boolean; saldo_pain: number
  conciliados: number; nao_conciliados: number; total: number
}
interface Lancamento {
  id: number; tipo: string; descricao: string; valor: number; data: string
  conciliado: boolean; conciliado_em: string | null
  conciliado_por_nome: string | null
  origem: string; observacao: string | null
  cg_codigo: string | null; cg_nome: string | null
  pessoa_nome: string | null
  conta_financeira_id: number | null
}

export default function ConciliacaoV2() {
  const qc = useQueryClient()
  const [contaId, setContaId] = useState<number | null>(null)
  const [filtroConc, setFiltroConc] = useState<'todos' | 'sim' | 'nao'>('nao')
  const [busca, setBusca] = useState('')
  const [dataIni, setDataIni] = useState('')
  const [dataFim, setDataFim] = useState('')
  const [selecionados, setSelecionados] = useState<Set<number>>(new Set())

  const { data: contas = [] } = useQuery<Conta[]>({
    queryKey: ['conciliacao-contas'],
    queryFn: async () => {
      const r = (await api.get('/financeiro/conciliacao-v2/contas')).data
      // seleciona MP padrão se ainda nada
      if (!contaId && r.length > 0) {
        const padrao = r.find((c: Conta) => c.padrao) || r[0]
        setContaId(padrao.id)
      }
      return r
    },
  })

  const contaSel = contas.find(c => c.id === contaId)

  const { data: lancamentos = [], isLoading } = useQuery<Lancamento[]>({
    queryKey: ['conciliacao-lancamentos', contaId, filtroConc, busca, dataIni, dataFim],
    queryFn: async () => {
      if (!contaId) return []
      const params: any = { conta_id: contaId, limit: 500 }
      if (filtroConc === 'sim') params.conciliado = true
      if (filtroConc === 'nao') params.conciliado = false
      if (busca) params.busca = busca
      if (dataIni) params.data_ini = dataIni
      if (dataFim) params.data_fim = dataFim
      return (await api.get('/financeiro/conciliacao-v2/lancamentos', { params })).data
    },
    enabled: !!contaId,
  })

  const { data: saldoMP } = useQuery<any>({
    queryKey: ['saldo-mp', contaSel?.padrao],
    queryFn: async () => (await api.get('/financeiro/conciliacao-v2/saldo-mp')).data,
    enabled: !!contaSel?.padrao,
  })

  const conciliarLote = useMutation({
    mutationFn: async () => (await api.post('/financeiro/conciliacao-v2/conciliar-lote',
      { ids: Array.from(selecionados) })).data,
    onSuccess: (r: any) => {
      toast.success(`${r.conciliados} lançamento(s) conciliado(s)`)
      setSelecionados(new Set())
      qc.invalidateQueries()
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro'),
  })
  const recalcularSaldos = useMutation({
    mutationFn: async () => (await api.post('/financeiro/conciliacao-v2/recalcular-saldos')).data,
    onSuccess: (r: any) => {
      toast.success(`Saldos recalculados: ${r.contas?.map((c: any) => `${c.nome}=${c.saldo.toFixed(2)}`).join(' · ')}`)
      qc.invalidateQueries()
    },
  })

  const desconciliar = useMutation({
    mutationFn: async (id: number) => (await api.post(`/financeiro/conciliacao-v2/desconciliar/${id}`)).data,
    onSuccess: () => {
      toast.success('Desconciliado')
      qc.invalidateQueries()
    },
  })
  const conciliarUm = useMutation({
    mutationFn: async (id: number) => (await api.post('/financeiro/conciliacao-v2/conciliar-lote',
      { ids: [id] })).data,
    onSuccess: () => {
      toast.success('Conciliado')
      qc.invalidateQueries()
    },
  })

  const totaisFiltrados = useMemo(() => {
    let creditos = 0, debitos = 0
    for (const l of lancamentos) {
      if (l.tipo === 'receita') creditos += l.valor
      else debitos += l.valor
    }
    return { creditos, debitos, saldo: creditos - debitos }
  }, [lancamentos])

  function toggleSel(id: number) {
    const s = new Set(selecionados)
    s.has(id) ? s.delete(id) : s.add(id)
    setSelecionados(s)
  }
  function toggleAll() {
    if (selecionados.size === lancamentos.filter(l => !l.conciliado).length) {
      setSelecionados(new Set())
    } else {
      setSelecionados(new Set(lancamentos.filter(l => !l.conciliado).map(l => l.id)))
    }
  }

  return (
    <div className="p-4 lg:p-6 space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Banknote size={22} className="text-violet-600" /> Conciliação Bancária
          </h1>
          <p className="text-xs text-ink-500">Confira cada lançamento do VELORE ERP com o que caiu no banco. Marque conciliado pra fechar.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => exportarExcel(`conciliacao-${new Date().toISOString().slice(0,10)}`, [
            { header: 'Data', key: 'data', fmt: (v: any) => (v || '').slice(0,10).split('-').reverse().join('/') },
            { header: 'Tipo', key: 'tipo' },
            { header: 'Descrição', key: 'descricao' },
            { header: 'Conta gerencial', key: 'cg_nome', fmt: (_: any, r: any) => [r.cg_codigo, r.cg_nome].filter(Boolean).join(' ') },
            { header: 'Pessoa', key: 'pessoa_nome' },
            { header: 'Valor', key: 'valor', fmt: (v: any) => numBR(v) },
            { header: 'Origem', key: 'origem' },
            { header: 'Conciliado', key: 'conciliado', fmt: (v: any) => v ? 'Sim' : 'Não' },
            { header: 'Conciliado em', key: 'conciliado_em', fmt: (v: any) => v ? String(v).slice(0,10).split('-').reverse().join('/') : '' },
          ], lancamentos)} disabled={!lancamentos.length}
            className="text-xs px-3 py-1.5 rounded border border-ink-300 hover:bg-ink-50 flex items-center gap-1.5 disabled:opacity-50">
            <FileSpreadsheet size={12} /> Gerar Excel
          </button>
          <button onClick={() => recalcularSaldos.mutate()} disabled={recalcularSaldos.isPending}
            className="text-xs px-3 py-1.5 rounded border border-ink-300 hover:bg-ink-50 flex items-center gap-1.5"
            title="Recalcula saldo das contas com base nos lançamentos">
            <Sparkles size={12} /> Recalcular saldos
          </button>
          <Link to="/financeiro/conciliacao-extrato"
            className="text-xs px-3 py-1.5 rounded border border-ink-300 hover:bg-ink-50 flex items-center gap-1.5">
            <FileText size={12} /> Importar extrato OFX/CSV
          </Link>
        </div>
      </div>

      {/* Selector de conta */}
      <div className="bg-white border border-ink-200 rounded-lg p-3">
        <div className="text-[10px] uppercase font-semibold text-ink-500 mb-2">Conta bancária</div>
        <div className="flex flex-wrap gap-2">
          {contas.map(c => (
            <button key={c.id} onClick={() => setContaId(c.id)}
              className={`text-xs px-3 py-2 rounded border-2 transition flex items-center gap-2 ${
                contaId === c.id
                  ? 'border-violet-600 bg-violet-50'
                  : 'border-ink-200 bg-white hover:border-ink-300'
              }`}>
              <Banknote size={13} className={contaId === c.id ? 'text-violet-600' : 'text-ink-400'} />
              <div className="text-left">
                <div className="font-semibold flex items-center gap-1">
                  {c.nome}
                  {c.padrao && <span className="text-[9px] px-1 py-0.5 rounded bg-violet-200 text-violet-800">PADRÃO</span>}
                </div>
                <div className="text-[10px] text-ink-500 font-mono">
                  {formatMoney(c.saldo_pain)} · {c.nao_conciliados} pendente(s)
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>

      {!contaSel ? (
        <div className="p-12 text-center text-ink-400">Selecione uma conta acima</div>
      ) : (
        <>
          <SaldosCompara conta={contaSel} saldoMP={saldoMP} />



          {/* Filtros */}
          <div className="bg-white border border-ink-200 rounded-lg p-3 space-y-2">
            <div className="flex flex-wrap gap-2 items-center">
              <div className="relative flex-1 min-w-[220px] max-w-md">
                <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-ink-400" />
                <input className="input pl-7 text-xs h-8" placeholder="Descrição, observação, cliente..."
                  value={busca} onChange={e => setBusca(e.target.value)} />
              </div>
              <div className="flex items-center gap-1 text-xs">
                <Calendar size={12} className="text-ink-400" />
                <input type="date" value={dataIni} onChange={e => setDataIni(e.target.value)}
                  className="input text-xs h-8 w-36" />
                <span className="text-ink-400">até</span>
                <input type="date" value={dataFim} onChange={e => setDataFim(e.target.value)}
                  className="input text-xs h-8 w-36" />
              </div>
              {(busca || dataIni || dataFim) && (
                <button onClick={() => { setBusca(''); setDataIni(''); setDataFim('') }}
                  className="text-xs px-2 py-1 rounded text-ink-500 hover:bg-ink-100">Limpar</button>
              )}
            </div>
            <div className="flex items-center gap-1">
              {[
                { v: 'nao', l: 'Não conciliados' },
                { v: 'sim', l: 'Conciliados' },
                { v: 'todos', l: 'Todos' },
              ].map(o => (
                <button key={o.v} onClick={() => setFiltroConc(o.v as any)}
                  className={`text-xs px-3 py-1 rounded ${
                    filtroConc === o.v ? 'bg-violet-600 text-white' : 'bg-ink-100 hover:bg-ink-200 text-ink-700'
                  }`}>
                  {o.l}
                </button>
              ))}
            </div>
          </div>

          {/* Resumo do filtro */}
          {lancamentos.length > 0 && (
            <div className="grid grid-cols-3 gap-2 text-xs">
              <div className="bg-emerald-50 border border-emerald-200 rounded p-2">
                <div className="text-[9px] uppercase font-semibold text-emerald-900">Créditos</div>
                <div className="text-base font-bold font-mono text-emerald-800">{formatMoney(totaisFiltrados.creditos)}</div>
              </div>
              <div className="bg-rose-50 border border-rose-200 rounded p-2">
                <div className="text-[9px] uppercase font-semibold text-rose-900">Débitos</div>
                <div className="text-base font-bold font-mono text-rose-800">{formatMoney(totaisFiltrados.debitos)}</div>
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded p-2">
                <div className="text-[9px] uppercase font-semibold text-blue-900">Saldo do filtro</div>
                <div className="text-base font-bold font-mono text-blue-800">{formatMoney(totaisFiltrados.saldo)}</div>
              </div>
            </div>
          )}

          {/* Ação em lote */}
          {selecionados.size > 0 && (
            <div className="bg-violet-100 border border-violet-300 rounded-lg p-2 flex items-center justify-between">
              <span className="text-xs font-semibold text-violet-900">{selecionados.size} selecionado(s)</span>
              <button onClick={() => conciliarLote.mutate()} disabled={conciliarLote.isPending}
                className="btn-primary text-xs flex items-center gap-1.5">
                <CheckCircle2 size={12} /> Conciliar selecionados
              </button>
            </div>
          )}

          {/* Tabela de lançamentos */}
          <div className="bg-white border border-ink-200 rounded-lg overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-ink-50 sticky top-0">
                <tr className="text-[10px] uppercase font-bold text-ink-600">
                  <th className="p-2 w-8">
                    <input type="checkbox"
                      checked={lancamentos.length > 0 && selecionados.size === lancamentos.filter(l => !l.conciliado).length}
                      onChange={toggleAll} />
                  </th>
                  <th className="text-left p-2">Data</th>
                  <th className="text-left p-2">Tipo</th>
                  <th className="text-left p-2">Descrição</th>
                  <th className="text-left p-2">Conta gerencial</th>
                  <th className="text-left p-2">Pessoa</th>
                  <th className="text-right p-2">Valor</th>
                  <th className="text-left p-2">Origem</th>
                  <th className="text-center p-2">Status</th>
                  <th className="text-center p-2">Ação</th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr><td colSpan={10} className="p-8 text-center text-ink-400">Carregando...</td></tr>
                ) : lancamentos.length === 0 ? (
                  <tr><td colSpan={10} className="p-8 text-center text-ink-400">Sem lançamentos com os filtros atuais</td></tr>
                ) : lancamentos.map(l => (
                  <tr key={l.id} className={`border-t border-ink-100 hover:bg-blue-50/30 ${l.conciliado ? 'opacity-70' : ''}`}>
                    <td className="p-2">
                      {!l.conciliado && (
                        <input type="checkbox" checked={selecionados.has(l.id)}
                          onChange={() => toggleSel(l.id)} />
                      )}
                    </td>
                    <td className="p-2 font-mono text-[10px]">{new Date(l.data).toLocaleDateString('pt-BR')}</td>
                    <td className="p-2">
                      {l.tipo === 'receita' ? (
                        <ArrowUpCircle size={12} className="text-emerald-600 inline" />
                      ) : (
                        <ArrowDownCircle size={12} className="text-rose-600 inline" />
                      )}
                    </td>
                    <td className="p-2 max-w-md truncate">{l.descricao}</td>
                    <td className="p-2 text-[10px] text-ink-500">
                      {l.cg_codigo && <><span className="font-mono">{l.cg_codigo}</span> {l.cg_nome}</>}
                    </td>
                    <td className="p-2 text-[10px] truncate max-w-[140px]">{l.pessoa_nome || '—'}</td>
                    <td className={`text-right p-2 font-mono font-semibold ${l.tipo === 'receita' ? 'text-emerald-700' : 'text-rose-700'}`}>
                      {l.tipo === 'receita' ? '+' : '−'}{formatMoney(l.valor)}
                    </td>
                    <td className="p-2 text-[10px]">
                      <span className={`px-1.5 py-0.5 rounded text-[9px] uppercase font-semibold ${
                        l.origem === 'auto_mp' ? 'bg-violet-100 text-violet-800'
                          : l.origem === 'baixa' || l.origem === 'baixa_marketplace' ? 'bg-blue-100 text-blue-800'
                          : 'bg-ink-100 text-ink-700'
                      }`}>{l.origem}</span>
                    </td>
                    <td className="text-center p-2">
                      {l.conciliado ? (
                        <span className="text-[10px] text-emerald-700 flex items-center justify-center gap-1"
                          title={`Por ${l.conciliado_por_nome || '—'} em ${l.conciliado_em ? new Date(l.conciliado_em).toLocaleString('pt-BR') : ''}`}>
                          <CheckCircle2 size={11} /> conciliado
                        </span>
                      ) : (
                        <span className="text-[10px] text-amber-700">pendente</span>
                      )}
                    </td>
                    <td className="text-center p-2">
                      {l.conciliado ? (
                        <button onClick={() => desconciliar.mutate(l.id)}
                          className="text-[10px] px-2 py-1 rounded text-ink-600 hover:bg-ink-100 flex items-center gap-1 mx-auto"
                          title="Desfaz conciliação">
                          <Undo2 size={10} />
                        </button>
                      ) : (
                        <button onClick={() => conciliarUm.mutate(l.id)}
                          className="text-[10px] px-2 py-1 rounded bg-emerald-100 text-emerald-800 hover:bg-emerald-200 flex items-center gap-1 mx-auto">
                          <CheckCircle2 size={10} /> Conciliar
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  )
}

function SaldosCompara({ conta, saldoMP }: any) {
  const chaveSaldoBanco = `pain_saldo_banco_${conta.id}`
  const chaveSaldoBancoData = `pain_saldo_banco_data_${conta.id}`
  const [saldoBanco, setSaldoBanco] = useState<string>(() => {
    try { return localStorage.getItem(chaveSaldoBanco) || '' } catch { return '' }
  })
  const [dataInfo, setDataInfo] = useState<string>(() => {
    try { return localStorage.getItem(chaveSaldoBancoData) || '' } catch { return '' }
  })
  const [editando, setEditando] = useState(false)

  function salvar() {
    try {
      localStorage.setItem(chaveSaldoBanco, saldoBanco)
      localStorage.setItem(chaveSaldoBancoData, new Date().toISOString())
      setDataInfo(new Date().toISOString())
      setEditando(false)
      toast.success('Saldo informado salvo')
    } catch {}
  }

  // Tenta saldo real da API primeiro; se não tiver, usa o informado pelo user
  const saldoReal = saldoMP?.saldo != null ? saldoMP.saldo : (saldoBanco ? parseFloat(saldoBanco) : null)
  const fonteSaldoReal = saldoMP?.saldo != null ? 'API' : (saldoBanco ? 'Informado' : null)
  const dif = saldoReal != null ? conta.saldo_pain - saldoReal : null

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
      <CardSaldo
        titulo="Saldo VELORE ERP (calculado)"
        valor={conta.saldo_pain}
        sub={`${conta.conciliados} conciliados · ${conta.nao_conciliados} pendentes`}
        cor="violet"
      />

      <div className={`border-2 rounded-lg p-3 ${
        saldoReal == null ? 'bg-amber-50 border-amber-300 text-amber-900' : 'bg-emerald-50 border-emerald-300 text-emerald-900'
      }`}>
        <div className="text-[10px] uppercase font-bold opacity-70 flex items-center justify-between">
          <span>{conta.padrao ? 'Saldo Mercado Pago' : `Saldo ${conta.banco || conta.nome}`}</span>
          {fonteSaldoReal && <span className="text-[9px] opacity-60">({fonteSaldoReal})</span>}
        </div>
        {editando || saldoBanco === '' ? (
          <div className="mt-1 flex gap-1">
            <input type="number" step="0.01"
              placeholder="Digite o saldo atual do banco"
              value={saldoBanco}
              onChange={e => setSaldoBanco(e.target.value)}
              className="input text-base font-mono h-9 flex-1" />
            <button onClick={salvar} className="text-xs px-2 rounded bg-emerald-600 text-white hover:bg-emerald-700">
              <CheckCircle2 size={12} />
            </button>
          </div>
        ) : (
          <div className="flex items-center justify-between mt-1">
            <div className="text-xl font-bold font-mono">{saldoReal != null ? formatMoney(saldoReal) : '—'}</div>
            <button onClick={() => setEditando(true)} className="text-[10px] underline opacity-70">editar</button>
          </div>
        )}
        {dataInfo && !editando && (
          <div className="text-[10px] opacity-60 mt-1">
            Informado em {new Date(dataInfo).toLocaleString('pt-BR')}
          </div>
        )}
        {saldoMP?.saldo == null && !editando && saldoBanco === '' && (
          <div className="text-[10px] opacity-70 mt-1">
            Acesse o app/site do banco e digite aqui pra comparar
          </div>
        )}
      </div>

      <div className={`border-2 rounded-lg p-3 ${
        dif == null ? 'bg-ink-50 border-ink-200 text-ink-700'
          : Math.abs(dif) < 0.5 ? 'bg-emerald-50 border-emerald-300 text-emerald-900'
          : 'bg-rose-50 border-rose-300 text-rose-900'
      }`}>
        <div className="text-[10px] uppercase font-bold opacity-70">Diferença</div>
        <div className="text-xl font-bold font-mono mt-1">
          {dif != null ? `${dif > 0 ? '+' : dif < 0 ? '−' : ''}${formatMoney(Math.abs(dif))}` : '—'}
        </div>
        <div className="text-[10px] opacity-70 mt-1">
          {dif == null ? 'Informe saldo do banco pra comparar'
            : Math.abs(dif) < 0.5 ? '✓ VELORE ERP bate com banco!'
            : dif > 0 ? 'VELORE ERP tem MAIS — checar lançamentos a remover/duplicar'
            : 'VELORE ERP tem MENOS — checar lançamentos faltando'}
        </div>
      </div>
    </div>
  )
}

function CardSaldo({ titulo, valor, sub, cor, alerta }: any) {
  const cores: any = {
    violet: 'bg-violet-50 border-violet-300 text-violet-900',
    emerald: 'bg-emerald-50 border-emerald-300 text-emerald-900',
    amber: 'bg-amber-50 border-amber-300 text-amber-900',
  }
  return (
    <div className={`border-2 rounded-lg p-3 ${cores[cor] || cores.violet}`}>
      <div className="text-[10px] uppercase font-bold opacity-70 flex items-center gap-1">
        {titulo}
        {alerta && <AlertCircle size={10} />}
      </div>
      <div className="text-xl font-bold font-mono mt-1">
        {valor != null ? formatMoney(valor) : '—'}
      </div>
      {sub && <div className="text-[10px] opacity-70 mt-1">{sub}</div>}
    </div>
  )
}
